import { ThemeKey } from "@smartsheet/design-tokens";
import classNames from "classnames";
import React from "react";
import { layers } from "../../constants/layers";
import { noop } from "../../constants/noop";
import { getThemeClasses, useTheme } from "../../theme";
import { BlanketStyled } from "./styled/BlanketStyled";

interface Props {
    /** Whether mouse events can pierce the blanket. If true, onClick will not be fired */
    canClickThrough?: boolean;

    /**
     * A classname to be applied to the root element of this component.
     */
    className?: string;

    /**
     A `clientId` prop is provided for specified elements, which is a unique string that
     appears as a data attribute `data-client-id` in the rendered code, serving as a hook
     for automated tests
     */
    clientId?: string;

    /**
     * Whether the blanket has a tinted background color.
     */
    isTinted?: boolean;

    /**
     * Handler function to be called when the blanket is clicked
     */
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

    /**
     * Handler function to be called when the blanket is clicked
     */
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

    /**
     * If specified, this will be used for the Blanket's z-index instead of the default
     * `layers.blanket`.
     */
    zIndex?: number;
}

/**
 * The main purpose of the blanket component is to provide the overlay layer for components such as
 * a modal dialog or menu.
 */
export const Blanket = ({
    canClickThrough = false,
    className,
    isTinted = false,
    onClick = noop,
    onDoubleClick = noop,
    clientId,
    zIndex = layers.blanket,
}: Props) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;

    const combinedClassName = classNames(getThemeClasses(themeMode), className); //this enables custom classNames while still including required theme classNames
    return (
        <BlanketStyled
            canClickThrough={canClickThrough}
            className={combinedClassName}
            data-client-id={clientId}
            isTinted={isTinted}
            onClick={canClickThrough ? undefined : onClick}
            onDoubleClick={canClickThrough ? undefined : onDoubleClick}
            themeMode={themeMode}
            zIndex={zIndex}
        />
    );
};
